import React, { useEffect, useRef, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from '@material-ui/core/Button';
import utils from './utils';

/**
 * @typedef {Object} Mesa
 * @property {number} [id]
 * @property {string} codigo
 * @property {number|string} coluna  
 * @property {string} [descricao]
 * @property {boolean} disponivel
 * @property {number} fila
 * @property {number} [id_evento]
 * @property {boolean} reservada_diretoria
 * @property {boolean} [noDesk]
 */

const COLUMNS_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'];
const DEFAULT_NO_DESK = ['A12', 'A13', 'A14', 'A15', 'A16', 'A17'];

/**
 * Retorna a letra correspondente ao índice da coluna (1-12)
 */
function getColLetter(colIdx) {
  return COLUMNS_LETTERS[colIdx - 1] || '';
}

/**
 * Compara se duas mesas são equivalentes
 */
function isSameMesa(m1, m2) {
  if (!m1 || !m2) return false;
  if (m1.id && m2.id) return m1.id === m2.id;
  return m1.codigo === m2.codigo;
}

/**
 * Calcula o limite de convites de mesa por cliente a partir de um registro de
 * `listar_limitacao_compra_by_sku`. Retorna 0 quando não há limite ativo.
 *
 * @param {Object} [result] registro retornado pelo endpoint
 * @returns {number}
 */
export function getLimiteMesasPorCliente(result) {
  const dataFimLimiteVendaMesas = result?.data_fim_limite_venda_mesas;
  const limitePorCliente = result?.limite_mesas_por_cliente;
  const limiteExpirado = dataFimLimiteVendaMesas ? new Date() >= new Date(dataFimLimiteVendaMesas) : false;

  return limitePorCliente > 0 && !limiteExpirado ? limitePorCliente : 0;
}

export default function DialogMesasEvento(props) {
  const { open, onClose, id_pedido, quantidade, id_sku, crud, mesasReservadas, onSave, onQuantidadeAlterada } = props;

  /** @type {[Mesa[], React.Dispatch<React.SetStateAction<Mesa[]>>]} */
  const [mesas, setMesas] = useState([]);
  const [selectedMesas, setSelectedMesas] = useState(mesasReservadas || []);
  const [itemId, setItemId] = useState();
  const [mesaPendente, setMesaPendente] = useState(null);
  const [confirmandoReducao, setConfirmandoReducao] = useState(false);
  const [salvandoQuantidade, setSalvandoQuantidade] = useState(false);
  const [limiteMesas, setLimiteMesas] = useState(0);
  const qtdEnviadaRef = useRef(0);
  const timeoutSalvandoRef = useRef(null);
  const abertoAnteriorRef = useRef(false);

  const getQuantidadeEfetiva = () => Math.max(Number(quantidade) || 0, qtdEnviadaRef.current);

  useEffect(() => {
    if (open) {
      if (id_pedido) {
        utils.execute(crud || {}, 'listaItemByPedVenda?id_hub_ped_venda=' + id_pedido, null, function (itemVenda) {
          const item = itemVenda?.data?.[0];

          if (!item) return;

          if (item.id) {
            setItemId(item.id);
          }

          if (!!item.id) {
            utils.execute(
              crud || {},
              `listarMesasEvento?id_hub_ped_venda_item=${item.id}&size=-1`,
              null,
              function (res) {
                setMesas(res?.data || []);
              },
            );
          }
        });
      }
    }
  }, [open, id_pedido, quantidade, crud]);

  useEffect(() => {
    if (open && !abertoAnteriorRef.current) {
      setSelectedMesas(mesasReservadas || []);
      setMesaPendente(null);
      setConfirmandoReducao(false);
      setSalvandoQuantidade(false);
      qtdEnviadaRef.current = 0;
    }

    abertoAnteriorRef.current = open;
  }, [open, mesasReservadas]);

  // Carrega o limite de convites de mesa por cliente
  useEffect(() => {
    if (!open) return;

    if (!id_sku) {
      setLimiteMesas(0);
      return;
    }

    utils.execute(crud || {}, 'listar_limitacao_compra_by_sku?id_sku=' + id_sku, null, function (res) {
      setLimiteMesas(getLimiteMesasPorCliente(res?.data?.[0]));
    });
  }, [open, id_sku, crud]);

  const limparTimeoutSalvando = () => {
    if (timeoutSalvandoRef.current) {
      clearTimeout(timeoutSalvandoRef.current);
      timeoutSalvandoRef.current = null;
    }
  };

  useEffect(() => {
    return () => limparTimeoutSalvando();
  }, []);

  const handleSelectMesa = (mesa) => {
    if (!mesa.id) {
      let mensagem_error = utils.lng(
        'erro_mesa_sem_id',
        'A mesa [MESA_COD] não possui um identificador válido. Por favor, tente novamente ou contate o suporte.',
      );

      mensagem_error = mensagem_error.replace('[MESA_COD]', mesa.codigo || '"desconhecida"');

      window.dialog.alert(mensagem_error);
      return;
    }

    if (selectedMesas.length >= getQuantidadeEfetiva()) {
      // Sem o sku não há como acrescentar o convite, então mantém o bloqueio simples
      if (!id_sku) {
        window.dialog.alert(
          utils.lng(
            'erro_limite_mesas_bloqueado',
            'Há mais mesas selecionadas do que a quantidade informada no carrinho. Remova mesas da seleção ou aumente a quantidade no carrinho para continuar.',
          ),
        );
        return;
      }

      if (limiteMesas > 0 && getQuantidadeEfetiva() + 1 > limiteMesas) {
        let mensagem_error = utils.lng(
          'erro_limite_convites_cliente',
          'Seu usuário está limitado a [LIMITE] convite(s) de mesa(s). Remova mesas da seleção para continuar.',
        );

        mensagem_error = mensagem_error.replace('[LIMITE]', limiteMesas);

        window.dialog.alert(mensagem_error);
        return;
      }

      setMesaPendente(mesa);
      return;
    }

    setSelectedMesas((prev) => [...prev, mesa]);
  };

  const handleRejeitarConvite = () => {
    if (salvandoQuantidade) return;

    setMesaPendente(null);
  };

  const alterarQuantidadeConvite = (novaQuantidade, { onSucesso, mensagemErroPadrao }) => {
    setSalvandoQuantidade(true);

    limparTimeoutSalvando();
    timeoutSalvandoRef.current = setTimeout(function () {
      timeoutSalvandoRef.current = null;
      setSalvandoQuantidade(false);
    }, 20000);

    const _ks_ = {};

    _ks_.crud = crud || {};
    _ks_.changed = { quantidade: novaQuantidade };
    _ks_.element = {};
    _ks_.global = true;

    _ks_.complete = function (res) {
      limparTimeoutSalvando();
      setSalvandoQuantidade(false);

      if (!res || res.error === true || (res.errors && res.errors.length > 0) || res.status >= 400) {
        const mensagemServidor = res && res.message ? utils.lng(res.message, res.message) : '';

        window.dialog.alert(mensagemServidor || mensagemErroPadrao);
        return;
      }

      qtdEnviadaRef.current = novaQuantidade;

      if (typeof onQuantidadeAlterada === 'function') {
        onQuantidadeAlterada();
      }

      onSucesso();
    };

    _ks_.cfg = {
      alert: false,
      global: true,
      url: 'cart/changeProduct/' + id_sku,
      method: 'GET',
    };

    utils.call(_ks_);
  };

  const handleAceitarConvite = () => {
    const mesa = mesaPendente;

    if (!mesa || salvandoQuantidade) return;

    alterarQuantidadeConvite(getQuantidadeEfetiva() + 1, {
      mensagemErroPadrao: utils.lng('erro_add_convite_mesa', 'Não foi possível adicionar o convite ao seu carrinho.'),
      onSucesso: () => {
        setSelectedMesas((prev) => [...prev, mesa]);
        setMesaPendente(null);
      },
    });
  };

  const handleCancelarReducaoQuantidade = () => {
    if (salvandoQuantidade) return;

    setConfirmandoReducao(false);
  };

  const handleConfirmarReducaoQuantidade = () => {
    if (salvandoQuantidade) return;

    alterarQuantidadeConvite(selectedMesas.length, {
      mensagemErroPadrao: utils.lng(
        'erro_reduzir_convite_mesa',
        'Não foi possível reduzir a quantidade do seu carrinho.',
      ),
      onSucesso: () => {
        setConfirmandoReducao(false);
        persistirReservaMesas(selectedMesas);
      },
    });
  };

  const handleDeselectMesa = (mesa) => {
    setSelectedMesas((prev) => prev.filter((m) => !isSameMesa(m, mesa)));
  };

  const persistirReservaMesas = (mesasParaReservar) => {
    const listaMesas = mesasParaReservar.map((m) => m.id);

    const _ks_ = {};
    _ks_.crud = crud;
    _ks_.changed = {
      lista_mesas: listaMesas,
    };
    _ks_.complete = function (res) {
      if (res && (res.error || res.status >= 400)) {
        window.dialog.alert(
          res.message || res.error || utils.lng('erro_selecao_mesas', 'Ocorreu um erro ao reservar as mesas.'),
        );
        return;
      }

      window.dialog.alert(utils.lng('mesas_reservadas_sucesso', 'Mesas reservadas com sucesso!'), null, [
        {
          label: utils.lng('fechar', 'Fechar'),
          action: function () {
            if (typeof onSave === 'function') onSave(mesasParaReservar);
            onClose();
          },
        },
      ]);
    };

    _ks_.cfg = {
      url: `cart/event/table/reserve?id_hub_pedido_venda_item=${itemId}`,
      method: 'POST',
    };

    utils.call(_ks_);
  };

  const handleSave = () => {
    const quantidadeEfetiva = getQuantidadeEfetiva();

    if (selectedMesas.length === quantidadeEfetiva) {
      persistirReservaMesas(selectedMesas);
      return;
    }

    if (selectedMesas.length < quantidadeEfetiva && id_sku) {
      setConfirmandoReducao(true);
      return;
    }

    let mensagem_error = utils.lng(
      'erro_selecao_mesas_quantidade',
      'Você informou [QTD_SOLIC] mesa(s) no carrinho, mas selecionou apenas [QTD_SEL]. Selecione mais mesas ou reduza a quantidade no carrinho para continuar.',
    );

    mensagem_error = mensagem_error
      .replace('[QTD_SOLIC]', quantidadeEfetiva)
      .replace('[QTD_SEL]', selectedMesas.length);

    window.dialog.alert(mensagem_error);
  };

  const handleClose = () => {
    setSelectedMesas(mesasReservadas || []);
    onClose();
  };

  // Renderização da Grid completa (12 Colunas: A..L, 17 Linhas: 1..17)
  const renderGridCells = () => {
    const cells = [];
    for (let f = 0; f <= 17; f++) {
      for (let c = 0; c <= 12; c++) {
        const gridCol = c <= 6 ? c + 1 : c + 2;
        const gridRow = f <= 11 ? f + 1 : f + 2;
        const colLetter = getColLetter(c);
        const code = `${colLetter}${f}`;

        // Canto superior esquerdo vazio
        if (c === 0 && f === 0) continue;

        // Número da fila
        if (c === 0) {
          cells.push(
            <div key={`fila-${f}`} className="mesa-celula mesa-fila" style={{ gridColumn: 0, gridRow: gridRow }}>
              <span className="mesa-indice">{f}</span>
            </div>,
          );
          continue;
        }

        // Letra da coluna
        if (f === 0) {
          cells.push(
            <div key={`coluna-${c}`} className="mesa-celula mesa-coluna" style={{ gridColumn: gridCol, gridRow: 0 }}>
              <span className="mesa-indice">{colLetter}</span>
            </div>,
          );
          continue;
        }

        // Busca se existe uma mesa recebida no state mesas
        const mesaMatch = mesas.find((m) => m.codigo === code);

        // Se a mesa possuir a flag noDesk ou for uma das posições noDesk padrão
        const isNoDesk = DEFAULT_NO_DESK.includes(code) || mesaMatch?.noDesk === true;

        if (isNoDesk) {
          cells.push(
            <div
              key={`vazia-${c}-${f}`}
              className="mesa-celula mesa-vazia"
              style={{ gridColumn: gridCol, gridRow: gridRow }}
            />,
          );
          continue;
        }

        // Utiliza os dados recebidos da mesa ou gera o objeto default para a posição
        const mesaObj = mesaMatch || {
          codigo: code,
          coluna: c,
          fila: f,
          descricao: code,
          disponivel: true,
          reservada_diretoria: false,
        };

        const isReservada = mesaObj.reservada_diretoria === true;
        const isSelected = selectedMesas.some((m) => isSameMesa(m, mesaObj));
        const isDisponivel = mesaObj.disponivel !== false;

        let className = 'mesa-celula ';
        let cellText = code;
        let onClickHandler = undefined;

        if (isReservada) {
          className += 'mesa-reservada';
        } else if (isSelected) {
          className += 'mesa-selecionada';
          onClickHandler = () => handleDeselectMesa(mesaObj);
        } else if (!isDisponivel) {
          className += 'mesa-indisponivel';
        } else {
          className += 'mesa-disponivel';
          onClickHandler = () => handleSelectMesa(mesaObj);
        }

        cells.push(
          <div
            key={`mesa-${c}-${f}`}
            className={className}
            style={{ gridColumn: gridCol, gridRow: gridRow }}
            onClick={onClickHandler}
          >
            {cellText}
          </div>,
        );
      }
    }
    return cells;
  };

  return (
    <>
      <Dialog open={Boolean(open)} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>{utils.lng('selecionar_mesas', 'Selecionar Mesas')}</DialogTitle>
        <DialogContent>
          <div className="mesas-grid-container">
            <div className="mesas-palco">{utils.lng('palco', 'Palco')}</div>
            <div className="mesas-grid">{renderGridCells()}</div>
          </div>
        </DialogContent>
        <DialogActions className="dialog-mesas-actions">
          <div className="dialog-mesas-actions-center">
            <Button onClick={handleClose} variant="outlined" color="primary" disabled={salvandoQuantidade}>
              {utils.lng('fechar', 'Fechar')}
            </Button>
            <Button onClick={handleSave} variant="contained" color="primary" disabled={salvandoQuantidade}>
              {utils.lng('salvar', 'Salvar')}
            </Button>
          </div>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(mesaPendente)} onClose={handleRejeitarConvite} maxWidth="xs" fullWidth>
        <DialogTitle>{utils.lng('adicionar_convite_mesa', 'Adicionar convite')}</DialogTitle>
        <DialogContent>
          <span>
            {utils.lng(
              'erro_limite_mesas',
              'Você selecionou mais mesas do que o esperado. Deseja adicionar mais um convite ao seu carrinho? O valor do pedido será atualizado.',
            )}
          </span>
        </DialogContent>
        <DialogActions className="dialog-mesas-actions">
          <div className="dialog-mesas-actions-center">
            <Button onClick={handleRejeitarConvite} variant="contained" color="secondary" disabled={salvandoQuantidade}>
              {utils.lng('cancelar', 'Cancelar')}
            </Button>
            <Button onClick={handleAceitarConvite} variant="contained" color="primary" disabled={salvandoQuantidade}>
              {utils.lng('confirmar', 'Confirmar')}
            </Button>
          </div>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmandoReducao} onClose={handleCancelarReducaoQuantidade} maxWidth="xs" fullWidth>
        <DialogTitle>{utils.lng('reduzir_convite_mesa', 'Reduzir quantidade')}</DialogTitle>
        <DialogContent>
          <span>
            {utils.lng(
              'confirmar_reducao_convite_mesa',
              'Você selecionou menos mesas do que a quantidade no carrinho. Deseja reduzir a quantidade para a quantidade de mesas selecionada? O valor do pedido será atualizado.',
            )}
          </span>
        </DialogContent>
        <DialogActions className="dialog-mesas-actions">
          <div className="dialog-mesas-actions-center">
            <Button
              onClick={handleCancelarReducaoQuantidade}
              variant="contained"
              color="secondary"
              disabled={salvandoQuantidade}
            >
              {utils.lng('cancelar', 'Cancelar')}
            </Button>
            <Button
              onClick={handleConfirmarReducaoQuantidade}
              variant="contained"
              color="primary"
              disabled={salvandoQuantidade}
            >
              {utils.lng('confirmar', 'Confirmar')}
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </>
  );
}
